import { useClearReportViewOnLeave } from "../useClearReportViewOnLeave";
import { useNavigate } from "react-router-dom";
import { BackBtn } from "../../Users/components/logIncident/logIncident.style";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { GoPencil } from "react-icons/go";
import type { MenuProps } from "antd";
import { Button } from "antd";
import { FaBell } from "react-icons/fa6";

import styled from "styled-components";
import {
  useAnalyticsWrapper,
  useReportWrapper,
  useStoreWrapper,
} from "../../../../store/store";
import { BigBoldText } from "../../dashboard/home/home.style";
import moment from "moment";
import useAnalytics from "../../../../hooks/useAnalytics";
import { useEffect, useRef, useState } from "react";
import { Workbook } from "@progress/kendo-ooxml";
import { PDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { LayerContainer } from "./styles";
import {
  behaviorAnalysisDetailCols,
  behaviorIncidentListCols,
} from "../dummyData/colums";
import useIncident from "../../../../hooks/useIncident";
import useIncidentTypes from "../../../../hooks/useIncidentTypes";
import { viewClickedIncident } from "../../../../utils/helpers/incidentListUtils";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../utils/helpers/pdfCsvUtils";

// NOTE: FilterModal is shared across all analytics reports
// It lives in fall-analysis folder but is used by all report types
import FilterModal from "../fall-analysis/FilterModal";
import EmailSubscriptionForm from "./form/EmailSubscriptionForm";
import Modalant from "../../../../components/ModalA/Modalant";
import useSubscriptions from "../../../../hooks/useSubscriptions";
import { toast } from "react-toastify";

// Import section components
import SectionModal from "./sections/BehaviorAnalysisSectionModal";
import RestoredDrilldown from "../drilldown/RestoredDrilldown";
import useFormDataHelpers from "../../../../hooks/useFormDataHelpers";
import BehaviorIncidentList from "./sections/BehaviorIncidentList";
import BehaviorIncidentsByUnit from "./sections/BehaviorIncidentsByUnit";
import BehaviorTypeBreakdown from "./sections/BehaviorTypeBreakdown";
import BehaviorLocationBreakdown from "./sections/BehaviorLocationBreakdown";
import BehaviorDayOfWeek from "./sections/BehaviorDayOfWeek";
import BehaviorShiftOverview from "./sections/BehaviorShiftOverview";
import BehaviorRepeatIncidents from "./sections/BehaviorRepeatIncidents";

export const HeaderSection = styled.section`
  display: flex;
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;
export const DateModBtnViewBtnContainer = styled.div``;

export const TitleContainer = styled.div``;
export const ModifyComponent = styled.div`
  width: 30%;
  display: flex;
  flex-direction: column;
  position: relative;
`;
export const DateDisplayModifyBtnContainer = styled.div`
  border: 1px solid #d9d9d9;
`;
export const ModifyBtn = styled.button`
  color: #fff;
  border-left: none;
  border-right: 1px solid #d9d9d9;
  border-bottom: 1px solid #d9d9d9;
  background-color: #fff;
  cursor: pointer;
  background-color: var(--main-purple-light);
`;
const ActBtn = styled.button`
  color: #b580ff;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  cursor: pointer;
`;
export const ViewandExportReportBtnContainer = styled.div``;
export const ViewBtnsContainer = styled.div``;

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================
const BehaviorAnalysisReport = () => {
  useClearReportViewOnLeave("behavior");
  // ---------------------------------------------------------------------------
  // Refs for Export Components
  // ---------------------------------------------------------------------------
  const behaviorAnalysisPDFExportRef = useRef<PDFExport>(null);
  const excelExporter = useRef<ExcelExport>(null);

  // ---------------------------------------------------------------------------
  // Styled Components (local)
  // ---------------------------------------------------------------------------
  const ExportBtn = styled(Button)`
    background-color: var(--main-purple-light) !important;
    border-color: var(--main-purple-light) !important;
    color: #fff !important;
  `;

  // ---------------------------------------------------------------------------
  // Hooks and Helpers
  // ---------------------------------------------------------------------------
  const navigate = useNavigate();
  const { getFacilityLabel, getFacilityValue } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [clickedIncident, setClickedIncident] = useState<any>({});
  const [behaviorData, setBehaviorData] = useState<any>(null);
  const [incidentListData, setIncidentListData] = useState<any[]>([]);

  // Table data for Excel export
  const [incidentsByUnitTableData, setIncidentsByUnitTableData] = useState<any>(
    []
  );
  const [behaviorTypeTableData, setBehaviorTypeTableData] = useState<any>([]);
  const [locationTableData, setLocationTableData] = useState<any>([]);
  const [dayOfWeekTableData, setDayOfWeekTableData] = useState<any>([]);
  const [shiftTableData, setShiftTableData] = useState<any>([]);
  const [repeatIncidentsTableData, setRepeatIncidentsTableData] = useState<any>(
    []
  );

  const [modalData, setModalData] = useState<any>({
    viewModal: false,
    data: null,
    viewType: "",
  });
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);

  // ---------------------------------------------------------------------------
  // Store Access
  // ---------------------------------------------------------------------------
  const { createForm, updateForm, user } = useStoreWrapper((state) => ({
    createForm: state.createForm,
    updateForm: state.updateForm,
    user: state.user,
  }));

  const { setIncident } = useReportWrapper((state) => ({
    setIncident: state.setIncident,
  }));

  // ---------------------------------------------------------------------------
  // Incident Details Hook (for clicking a row to view details)
  // ---------------------------------------------------------------------------
  const { getIncidentDetailsWithId } = useIncident({
    selectedIncidentId: clickedIncident?.incidentId
      ? clickedIncident?.incidentId
      : clickedIncident?.IncidentId,
  });

  const {
    refetch: refetchSelectedIncident,
    isFetching: isFetchingSelectedIncident,
  } = getIncidentDetailsWithId;

  const { incidentTypes: incidentTypesObject } = useIncidentTypes();
  const { handlePostSubscription } = useSubscriptions({});
  const { isLoading: isLoadingSubscription, mutateAsync: doPostSubscription } =
    handlePostSubscription;

  // ---------------------------------------------------------------------------
  // Analytics State from Zustand - Behavior-specific state properties
  // ---------------------------------------------------------------------------
  const {
    startDate,
    endDate,
    behaviorFacility,
    behaviorAnalysisIncidents,
    reportType,
    globalAnalyticsSelectedBehaviorIncidentSubType,
    globalAnalyticsSelectedBehaviorIncidentType,
    // globalAnalyticsSelectedBehaviorType,
    // globalAnalyticsSelectedTargetOfAggression,
    // globalAnalyticsSelectedLocationUnit,
    setStartDate,
    setEndDate,
    setBehaviorFacility,
    setGlobalAnaylyticsFacility,
    setReportType,
  } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    behaviorFacility: state.behaviorFacility,
    behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    reportType: state.reportType,
    globalAnalyticsSelectedBehaviorIncidentSubType:
      state.globalAnalyticsSelectedBehaviorIncidentSubType,
    globalAnalyticsSelectedBehaviorIncidentType:
      state.globalAnalyticsSelectedBehaviorIncidentType,
    // globalAnalyticsSelectedBehaviorType:
    //   state.globalAnalyticsSelectedBehaviorType,
    // globalAnalyticsSelectedTargetOfAggression:
    //   state.globalAnalyticsSelectedTargetOfAggression,
    // globalAnalyticsSelectedLocationUnit:
    //   state.globalAnalyticsSelectedLocationUnit,
    setStartDate: state.setStartDate,
    setEndDate: state.setEndDate,
    setBehaviorFacility: state.setBehaviorFacility,
    setGlobalAnaylyticsFacility: state.setGlobalAnaylyticsFacility,
    setReportType: state.setReportType,
  }));

  // ---------------------------------------------------------------------------
  // Navigation: Back Button Handler
  // Clears state and returns to analytics landing page
  // ---------------------------------------------------------------------------
  const handleBack = () => {
    setEndDate("");
    setStartDate("");
    setBehaviorFacility(null);
    setReportType("");
    navigate("/user/analytics");
    setGlobalAnaylyticsFacility(null);
  };

  // ---------------------------------------------------------------------------
  // Set report type on mount (tells FilterModal which form fields to show)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setReportType("behavior-analysis");
  }, []);

  // ---------------------------------------------------------------------------
  // Redirect if no facility selected (user navigated directly to URL)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorFacility === null || behaviorFacility === undefined) {
      navigate("/user/analytics");
    }
  }, [behaviorFacility]);

  // ---------------------------------------------------------------------------
  // Optional: Fetch incident list for the facility
  // (Used by some reports for additional data)
  // ---------------------------------------------------------------------------
  const { getIncidentListAnalysis } = useAnalytics({
    facility: getFacilityValue(behaviorFacility),
    startDate: moment(startDate).format("yyyy-MM-DD"),
    endDate: moment(endDate).format("yyyy-MM-DD"),
  });

  const {
    data: incidentListAnalysisData,
    isFetching: isFetchingIncidentListAnalysis,
  } = getIncidentListAnalysis;

  const disableViewBtn = isFetchingIncidentListAnalysis;

  // ---------------------------------------------------------------------------
  // Process behavior analysis data when it arrives from API
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const _behaviorData = behaviorAnalysisIncidents;
      setBehaviorData(_behaviorData);
      // Pass incident list data directly (API now returns camelCase with incidentNumber)
      setIncidentListData(behaviorAnalysisIncidents?.incidentList || []);
      setModalData({
        viewModal: false,
        data: behaviorAnalysisIncidents?.incidentList,
        viewType: "incident-list",
      });
    }
  }, [behaviorAnalysisIncidents]);

  // ---------------------------------------------------------------------------
  // Handle clicking an incident row to view full details
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (
      clickedIncident?.incidentId?.length > 0 ||
      clickedIncident?.IncidentId?.length > 0
    ) {
      const refetchedData = async () => {
        try {
          const response = await refetchSelectedIncident();
          const selectedIncident = response?.data?.data?.data;
          const incidentType = incidentTypesObject.find(
            (incident: any) => incident.flag === selectedIncident.RecordTypeId
          );
          setIncident(selectedIncident);
          viewClickedIncident(
            createForm,
            updateForm,
            selectedIncident,
            incidentType,
            navigate
          );
        } catch (err) {
          console.log({ err });
        }
      };
      refetchedData();
    }
  }, [clickedIncident]);

  // ---------------------------------------------------------------------------
  // Subscribe Menu Items (Desktop View)
  // ---------------------------------------------------------------------------
  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Button
          onClick={(prev) => setShowSubscriptionModal((prev) => !prev)}
          className="flex w-full items-center border-0"
        >
          <FaBell /> Subscribe
        </Button>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // Handle clicking outside FilterModal to close it
  // ---------------------------------------------------------------------------
  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (showFilterModal && !target.closest(".modify-btn-container")) {
      setShowFilterModal(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showFilterModal]);

  // ---------------------------------------------------------------------------
  // Subscription Form Submission Handler
  // ---------------------------------------------------------------------------
  interface FormData {
    startDate: string;
    endDate: string;
    startTime: string;
    recurrence: string;
    description: string;
    weeklyDays?: string;
    monthlyDay?: string;
    periodType: string;
    periodLength: string;
  }

  const handleFormSubmit = async (data: FormData) => {
    const subscription = {
      Report_Type__c: "Behavior Analytics",
      Incident_Types__c:
        globalAnalyticsSelectedBehaviorIncidentType?.join(",") || "",
      behavior_incident_sub_types__c:
        globalAnalyticsSelectedBehaviorIncidentSubType?.join(",") || "",
      Start_Date__c: data.startDate,
      End_Date__c: data.endDate,
      Interval_Type__c: data.recurrence,
      Description__c: data.description,
      Trailing_Period__c: data.periodType,
      Trailing_Period_Quantity__c: data.periodLength,
      User__c: user.userId,
      Facility__c: getFacilityValue(behaviorFacility),
      ...(data.recurrence === "weekly" && {
        Day_Of_The_Week__c: data.weeklyDays,
      }),
      ...(data.recurrence === "monthly" && {
        Day_Of_The_Month__c: data.monthlyDay,
      }),
    };

    try {
      const res = await doPostSubscription({ subscription });
      toast.success(res.data.message);
      setShowSubscriptionModal(false);
    } catch (err) {
      console.log({ err });
    }
  };

  // ---------------------------------------------------------------------------
  // PDF Export Handler
  // Exports single PDF with incident list and all analysis sections
  // ---------------------------------------------------------------------------
  const triggerPDFDownload = () => {
    if (behaviorAnalysisPDFExportRef.current) {
      behaviorAnalysisPDFExportRef.current.save();
    }
  };

  // ---------------------------------------------------------------------------
  // Excel Export Handler
  // ---------------------------------------------------------------------------
  const handleExcelExport = () => {
    if (excelExporter.current) {
      const workbook = new Workbook();
      workbook.sheets = [
        {
          name: "Behavior Analysis",
          columns: [
            { autoWidth: true },
            { autoWidth: true },
            { autoWidth: true },
            { autoWidth: true },
            { autoWidth: true },
          ],
          rows: [
            {
              cells: [
                {
                  value: "Behavior Analysis Report",
                  bold: true,
                  fontSize: 16,
                  textAlign: "center" as const,
                },
                {
                  value: `${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format("MMM DD, YYYY")}`,
                  bold: true,
                },
              ],
            },
            { cells: [{ value: "" }] },
            // Incident List Section
            {
              cells: [
                {
                  value: "Behavior Incident List",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "RESIDENT NAME", bold: true, background: "#f0f0f0" },
                { value: "INCIDENT TYPE", bold: true, background: "#f0f0f0" },
                { value: "BEHAVIOR TYPE", bold: true, background: "#f0f0f0" },
                { value: "DATE", bold: true, background: "#f0f0f0" },
                { value: "TIME", bold: true, background: "#f0f0f0" },
                { value: "SHIFT", bold: true, background: "#f0f0f0" },
                { value: "LOCATION", bold: true, background: "#f0f0f0" },
                { value: "UNIT", bold: true, background: "#f0f0f0" },
                { value: "TARGET OF AGGRESSION", bold: true, background: "#f0f0f0" },
                { value: "INJURY", bold: true, background: "#f0f0f0" },
                { value: "DOH REPORTABLE", bold: true, background: "#f0f0f0" },
                { value: "HOSP", bold: true, background: "#f0f0f0" },
                { value: "STATUS", bold: true, background: "#f0f0f0" },
                { value: "INCIDENT #", bold: true, background: "#f0f0f0" },
              ],
            },
            ...incidentListData.map((row: any) => ({
              cells: [
                { value: row.residentName || "-", bold: false },
                { value: row.incidentType || "-", bold: false },
                { value: row.behaviorType || "-", bold: false },
                { value: row.date || "-", bold: false },
                { value: row.time || "-", bold: false },
                { value: row.generalShift || "-", bold: false },
                { value: row.location || "-", bold: false },
                { value: row.unit || "-", bold: false },
                { value: row.targetOfAggression || "-", bold: false },
                { value: row.injury || "-", bold: false },
                { value: row.dohReportable || "-", bold: false },
                { value: row.hosp || "-", bold: false },
                { value: row.status || "-", bold: false },
                { value: row.incidentNumber || "-", bold: false },
              ],
            })),
            { cells: [{ value: "" }] },
            { cells: [{ value: "" }] },
            // Incidents by Unit Section
            {
              cells: [
                {
                  value: "Incidents by Unit",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "UNIT", bold: true, background: "#f0f0f0" },
                { value: "COUNT", bold: true, background: "#f0f0f0" },
                { value: "PERCENTAGE", bold: true, background: "#f0f0f0" },
              ],
            },
            ...incidentsByUnitTableData.map((row: any) => ({
              cells: [
                { value: row.unit || row.locationUnit || "", bold: false },
                { value: row.count || 0, bold: false },
                { value: `${Math.round(row.percentage || 0)}%`, bold: false },
              ],
            })),
            { cells: [{ value: "" }] },
            // Behavior Type Breakdown Section
            {
              cells: [
                {
                  value: "Behavior Type Breakdown",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "BEHAVIOR TYPE", bold: true, background: "#f0f0f0" },
                { value: "COUNT", bold: true, background: "#f0f0f0" },
                { value: "PERCENTAGE", bold: true, background: "#f0f0f0" },
              ],
            },
            ...behaviorTypeTableData.map((row: any) => ({
              cells: [
                { value: row.behaviorType || "", bold: false },
                { value: row.count || 0, bold: false },
                { value: `${Math.round(row.percentage || 0)}%`, bold: false },
              ],
            })),
            { cells: [{ value: "" }] },
            // Location Breakdown Section
            {
              cells: [
                {
                  value: "Location Breakdown",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "LOCATION", bold: true, background: "#f0f0f0" },
                { value: "COUNT", bold: true, background: "#f0f0f0" },
                { value: "PERCENTAGE", bold: true, background: "#f0f0f0" },
              ],
            },
            ...locationTableData.map((row: any) => ({
              cells: [
                { value: row.location || "", bold: false },
                { value: row.count || 0, bold: false },
                { value: `${Math.round(row.percentage || 0)}%`, bold: false },
              ],
            })),
            { cells: [{ value: "" }] },
            // Day of Week Section
            {
              cells: [
                {
                  value: "Day of Week Analysis",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "DAY", bold: true, background: "#f0f0f0" },
                { value: "COUNT", bold: true, background: "#f0f0f0" },
                { value: "PERCENTAGE", bold: true, background: "#f0f0f0" },
              ],
            },
            ...dayOfWeekTableData.map((row: any) => ({
              cells: [
                { value: row.day || "", bold: false },
                { value: row.count || 0, bold: false },
                { value: `${Math.round(row.percentage || 0)}%`, bold: false },
              ],
            })),
            { cells: [{ value: "" }] },
            // Shift Overview Section
            {
              cells: [
                {
                  value: "Shift Overview",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "SHIFT", bold: true, background: "#f0f0f0" },
                { value: "COUNT", bold: true, background: "#f0f0f0" },
                { value: "PERCENTAGE", bold: true, background: "#f0f0f0" },
              ],
            },
            ...shiftTableData.map((row: any) => ({
              cells: [
                { value: row.shift || "", bold: false },
                { value: row.count || 0, bold: false },
                { value: `${Math.round(row.percentage || 0)}%`, bold: false },
              ],
            })),
            { cells: [{ value: "" }] },
            // Repeat Incidents Section
            {
              cells: [
                {
                  value: "Repeat Incidents",
                  bold: true,
                  fontSize: 14,
                },
              ],
            },
            {
              cells: [
                { value: "RESIDENT", bold: true, background: "#f0f0f0" },
                { value: "COUNT", bold: true, background: "#f0f0f0" },
                { value: "PERCENTAGE", bold: true, background: "#f0f0f0" },
              ],
            },
            ...repeatIncidentsTableData.map((row: any) => ({
              cells: [
                { value: row.residentName || "", bold: false },
                { value: row.count || 0, bold: false },
                { value: `${Math.round(row.percentage || 0)}%`, bold: false },
              ],
            })),
          ],
        },
      ];

      // Use ExcelExport save method with workbook
      (excelExporter.current as ExcelExport).save(workbook);
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div>
      {/* ------------------------------------------------------------------- */}
      {/* BACK BUTTON                                                         */}
      {/* ------------------------------------------------------------------- */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <BackBtn onClick={() => handleBack()}>
          <ArrowLeftIcon width="30px"></ArrowLeftIcon>
          Back to report categories
        </BackBtn>
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* HEADER SECTION                                                      */}
      {/* ------------------------------------------------------------------- */}
      <HeaderSection className="flex flex-col">
        <TitleContainer className="relative mt-7">
          <BigBoldText
            className="capitalize text-purple-400"
            data-testid="report-title"
          >
            {getFacilityLabel(behaviorFacility)} - Behavior Analysis
          </BigBoldText>
        </TitleContainer>

        <DateModBtnViewBtnContainer className="mt-3 flex flex-col justify-between md:flex-row">
          {/* Date Display + Modify Button */}
          <ModifyComponent className="modify-btn-container w-full md:w-2/5">
            <div className="flex flex-row">
              <DateDisplayModifyBtnContainer className="mb-4 flex h-10 w-full items-center justify-between border-t border-solid border-gray-200 pl-2">
                <p className="text-sm text-purple-400">
                  {moment(startDate).format("LL")} -{" "}
                  {moment(endDate).format("LL")}
                </p>
              </DateDisplayModifyBtnContainer>
              <ModifyBtn
                data-testid="modify-report-params-btn"
                onClick={() => setShowFilterModal((prev) => !prev)}
                className="flex h-10 flex-row items-center justify-between border border-gray-200 px-4 py-2 text-sm"
              >
                <GoPencil className="mr-2" />
                Modify
              </ModifyBtn>
            </div>

            {/* FilterModal - SHARED component from fall-analysis */}
            <FilterModal
              open={showFilterModal}
              subtitle="Advanced Behavior Analysis"
              closeFn={() => setShowFilterModal(false)}
              filterType="general"
              isModifyMode={showFilterModal}
            />
          </ModifyComponent>

          {/* Action Buttons: Incident List + Subscribe */}
          <ViewandExportReportBtnContainer className="flex h-10 items-end justify-between">
            <ViewBtnsContainer className="mr-2 flex gap-3">
              {/* Button to open the Incident List Modal */}
              {/* <ActBtn
                className="text-white h-10 rounded px-4 py-2 text-sm disabled:opacity-50"
                onClick={() =>
                  setModalData({
                    viewModal: true,
                    data: behaviorData?.incidentList,
                    viewType: "incident-list",
                  })
                }
                disabled={disableViewBtn}
              >
                Incident List
              </ActBtn> */}
              {/* Export Excel Button */}
              <ExportBtn
                className="k-button k-button-md k-button-solid k-rounded-md h-10"
                onClick={handleExcelExport}
              >
                Export Excel
              </ExportBtn>
              {/* Export PDF Button */}
              <ExportBtn
                className="k-button k-button-md k-button-solid k-rounded-md h-10"
                onClick={triggerPDFDownload}
              >
                Export PDF
              </ExportBtn>
              {/* Subscribe buttons for desktop */}
              <div className="hidden gap-2 lg:flex">
                {items?.map((item) => (
                  <ActBtn
                    className="text-white h-10 rounded px-4 py-2 text-sm disabled:opacity-50"
                    key={item?.key}
                    // @ts-ignore
                    onClick={item?.label?.props?.onClick}
                  >
                    {/* @ts-ignore */}
                    {item?.label?.props?.children}
                  </ActBtn>
                ))}
              </div>
            </ViewBtnsContainer>
          </ViewandExportReportBtnContainer>
        </DateModBtnViewBtnContainer>
      </HeaderSection>

      {/* ------------------------------------------------------------------- */}
      {/* REPORT SECTIONS (wrapped in LayerContainer)                         */}
      {/* Each section reads from behaviorAnalysisIncidents in Zustand        */}
      {/* ------------------------------------------------------------------- */}

      {/* Section 1: Behavior Incident List - Displayed on page with its own export */}
      <div className="relative top-7 mt-7">
        <LayerContainer
          id="behaviorIncidentList"
          className="border-zinc-300 shadow-md"
          style={{ marginTop: "10px" }}
        >
          <BehaviorIncidentList
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
          />
        </LayerContainer>

        {/* Section 2: Incidents by Unit (bar chart + grid) */}
        <LayerContainer
          id="incidentsByUnit"
          className="border-zinc-300 shadow-md"
        >
          <BehaviorIncidentsByUnit
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
            shareTableData={(data: any) => setIncidentsByUnitTableData(data)}
          />
        </LayerContainer>

        {/* Section 3: Behavior Type Breakdown */}
        <LayerContainer
          id="behaviorTypeBreakdown"
          className="border-zinc-300 shadow-md"
        >
          <BehaviorTypeBreakdown
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
            shareTableData={(data: any) => setBehaviorTypeTableData(data)}
          />
        </LayerContainer>

        {/* Section 4: Location Breakdown */}
        <LayerContainer
          id="locationBreakdown"
          className="border-zinc-300 shadow-md"
        >
          <BehaviorLocationBreakdown
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
            shareTableData={(data: any) => setLocationTableData(data)}
          />
        </LayerContainer>

        {/* Section 5: Day of Week */}
        <LayerContainer id="dayOfWeek" className="border-zinc-300 shadow-md">
          <BehaviorDayOfWeek
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
            shareTableData={(data: any) => setDayOfWeekTableData(data)}
          />
        </LayerContainer>

        {/* Section 6: Shift Overview */}
        <LayerContainer
          id="shiftOverview"
          className="border-zinc-300 shadow-md"
        >
          <BehaviorShiftOverview
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
            shareTableData={(data: any) => setShiftTableData(data)}
          />
        </LayerContainer>

        {/* Section 7: Repeat Incidents */}
        <LayerContainer
          id="repeatIncidents"
          className="border-zinc-300 shadow-md"
        >
          <BehaviorRepeatIncidents
            setClickedIncident={(item: any) => setClickedIncident(item)}
            isFetchingSelectedIncident={isFetchingSelectedIncident}
            shareTableData={(data: any) => setRepeatIncidentsTableData(data)}
          />
        </LayerContainer>
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* HIDDEN PDF EXPORT (Combined Report)                                */}
      {/* ------------------------------------------------------------------- */}
      <div style={{ position: "absolute", left: "-4000px", top: 0 }}>
        <div style={{ width: "2600px" }}>
          <PDFExport
            margin="0.5cm"
            ref={behaviorAnalysisPDFExportRef}
            fileName={`Behavior Analysis Report ${moment(startDate).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.pdf`}
            paperSize="A0"
            landscape={true}
            repeatHeaders={true}
          >
            <div id="behavior-analysis-module">
              {/* Report Header */}
              <div style={{ marginBottom: "16px", padding: "10px" }}>
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                  }}
                >
                  {getFacilityLabel(behaviorFacility)} - Behavior Analysis
                  Report
                </h2>
                <p style={{ fontSize: "14px", color: "#666" }}>
                  {moment(startDate).format("LL")} -{" "}
                  {moment(endDate).format("LL")}
                </p>
              </div>

              {/* Incident List Table */}
              <div style={{ marginBottom: "30px" }}>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "10px",
                    padding: "0 10px",
                  }}
                >
                  Behavior Incident List
                </h3>
                <Grid
                  data={incidentListData}
                  pageable={false}
                  sortable={false}
                  scrollable="none"
                  className="dynamic-grid"
                  style={{ fontSize: "11px" }}
                >
                  {behaviorIncidentListCols().map((col: any) => (
                    <GridColumn
                      key={col.accessor}
                      field={col.accessor}
                      title={col.Header}
                    />
                  ))}
                </Grid>
              </div>

              {/* Page Break */}
              <div className="pdfPageBreak" />

              {/* Incidents by Unit */}
              <LayerContainer
                id="incidentsByUnitPdf"
                className="border-zinc-300 shadow-md"
                style={{ marginBottom: "20px" }}
              >
                <BehaviorIncidentsByUnit
                  setClickedIncident={(item: any) => setClickedIncident(item)}
                  isFetchingSelectedIncident={isFetchingSelectedIncident}
                  shareTableData={(data: any) =>
                    setIncidentsByUnitTableData(data)
                  }
                />
              </LayerContainer>

              <div className="pdfPageBreak" />

              {/* Behavior Type Breakdown */}
              <LayerContainer
                id="behaviorTypeBreakdown"
                className="border-zinc-300 shadow-md"
                style={{ marginBottom: "20px" }}
              >
                <BehaviorTypeBreakdown
                  setClickedIncident={(item: any) => setClickedIncident(item)}
                  isFetchingSelectedIncident={isFetchingSelectedIncident}
                  shareTableData={(data: any) => setBehaviorTypeTableData(data)}
                />
              </LayerContainer>

              <div className="pdfPageBreak" />

              {/* Location Breakdown */}
              <LayerContainer
                id="locationBreakdown"
                className="border-zinc-300 shadow-md"
                style={{ marginBottom: "20px" }}
              >
                <BehaviorLocationBreakdown
                  setClickedIncident={(item: any) => setClickedIncident(item)}
                  isFetchingSelectedIncident={isFetchingSelectedIncident}
                  shareTableData={(data: any) => setLocationTableData(data)}
                />
              </LayerContainer>

              <div className="pdfPageBreak" />

              {/* Day of Week */}
              <LayerContainer
                id="dayOfWeek"
                className="border-zinc-300 shadow-md"
                style={{ marginBottom: "20px" }}
              >
                <BehaviorDayOfWeek
                  setClickedIncident={(item: any) => setClickedIncident(item)}
                  isFetchingSelectedIncident={isFetchingSelectedIncident}
                  shareTableData={(data: any) => setDayOfWeekTableData(data)}
                />
              </LayerContainer>

              <div className="pdfPageBreak" />

              {/* Shift Overview */}
              <LayerContainer
                id="shiftOverview"
                className="border-zinc-300 shadow-md"
                style={{ marginBottom: "20px" }}
              >
                <BehaviorShiftOverview
                  setClickedIncident={(item: any) => setClickedIncident(item)}
                  isFetchingSelectedIncident={isFetchingSelectedIncident}
                  shareTableData={(data: any) => setShiftTableData(data)}
                />
              </LayerContainer>

              <div className="pdfPageBreak" />

              {/* Repeat Incidents */}
              <LayerContainer
                id="repeatIncidents"
                className="border-zinc-300 shadow-md"
                style={{ marginBottom: "20px" }}
              >
                <BehaviorRepeatIncidents
                  setClickedIncident={(item: any) => setClickedIncident(item)}
                  isFetchingSelectedIncident={isFetchingSelectedIncident}
                  shareTableData={(data: any) =>
                    setRepeatIncidentsTableData(data)
                  }
                />
              </LayerContainer>
            </div>
          </PDFExport>
        </div>
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* SECTION MODAL (for Incident List button)                            */}
      {/* ------------------------------------------------------------------- */}
      <RestoredDrilldown report="behavior" Modal={SectionModal} />
      {modalData.viewModal ? (
        <SectionModal
          open={modalData.viewModal}
          facilityName={getFacilityLabel(behaviorFacility)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() =>
            setModalData({ viewModal: false, data: null, viewType: "" })
          }
          resetFn={() =>
            setModalData({ viewModal: false, data: null, viewType: "" })
          }
          isLoading={isFetchingSelectedIncident}
          tableData={modalData?.data}
          tableCols={behaviorAnalysisDetailCols()}
          title="Behavior Incident List"
          getUserCallback={(item: any) => {
            setClickedIncident(item);
          }}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData?.data,
              columnData: behaviorAnalysisDetailCols(),
              fileName: getFacilityLabel(behaviorFacility),
              startDate,
              endDate,
              subTitle: "Behavior Incident List",
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData?.data,
              behaviorAnalysisDetailCols(),
              getFacilityLabel(behaviorFacility)
            )
          }
        />
      ) : null}

      {/* ------------------------------------------------------------------- */}
      {/* SUBSCRIPTION MODAL                                                  */}
      {/* ------------------------------------------------------------------- */}
      {showSubscriptionModal && (
        <Modalant
          isLoading={isLoadingSubscription}
          className="items-center md:w-6/12"
          isOpen={showSubscriptionModal}
          isClose={() => setShowSubscriptionModal(false)}
        >
          <EmailSubscriptionForm
            handleSubmit={handleFormSubmit}
            handleClose={() => setShowSubscriptionModal(false)}
            isFacility={true}
            isLoading={isLoadingSubscription}
            title={"Behavior Analysis"}
          />
        </Modalant>
      )}

      {/* ------------------------------------------------------------------- */}
      {/* HIDDEN EXCEL EXPORT COMPONENT                                      */}
      {/* ------------------------------------------------------------------- */}
      <ExcelExport
        ref={excelExporter}
        data={[]}
        fileName={`Behavior Analysis Report ${moment(startDate).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.xlsx`}
      />
    </div>
  );
};

export default BehaviorAnalysisReport;
